import { z } from "zod";

/** UI + content locales. `de` is the fallback for missing translations. */
export const LOCALES = ["de", "en", "fr", "it"] as const;
export const Locale = z.enum(LOCALES);
export type Locale = z.infer<typeof Locale>;
export const DEFAULT_LOCALE: Locale = "de";

/** Translatable content. Every key optional; readers use `pickLocale`. */
export const I18nText = z
  .object({
    de: z.string().max(5000),
    en: z.string().max(5000),
    fr: z.string().max(5000),
    it: z.string().max(5000),
  })
  .partial()
  .strict();
export type I18nText = z.infer<typeof I18nText>;

const FALLBACK_ORDER: readonly Locale[] = ["de", "en", "fr", "it"];

/** Requested locale first, then de → en → fr → it. Empty strings count as missing. */
export function pickLocale(text: I18nText | null | undefined, locale: Locale): string | null {
  if (!text) return null;
  for (const l of [locale, ...FALLBACK_ORDER]) {
    const v = text[l];
    if (v && v.trim() !== "") return v;
  }
  return null;
}

/** WGS84 point. Always lat/lon objects on the wire — never [lon, lat] arrays. */
export const LatLon = z
  .object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
  })
  .strict();
export type LatLon = z.infer<typeof LatLon>;

/** [west, south, east, north] in WGS84 degrees. */
export const BBox = z
  .tuple([z.number(), z.number(), z.number(), z.number()])
  .refine(([w, s, e, n]) => w < e && s < n, "bbox must be [west, south, east, north]");
export type BBox = z.infer<typeof BBox>;

/** A translatable field that must have at least one non-blank translation (names). */
export const I18nTextRequired = I18nText.refine(
  (t) => pickLocale(t, "de") !== null,
  "at least one non-blank translation",
);

/**
 * Strict numeric query param: rejects "", whitespace, booleans and arrays
 * (z.coerce.number() would turn them into 0/1).
 */
export const NumParam = z
  .string()
  .trim()
  .regex(/^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i, "number")
  .transform(Number);

export const Id = z.uuid();
/** `:id` path param for spots and objects (UUIDs). Users have their own `UserIdParam`. */
export const IdParam = z.object({ id: Id }).strict();
export const Slug = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "lowercase-kebab-case");
const TRANSLIT: Record<string, string> = {
  ä: "ae",
  ö: "oe",
  ü: "ue",
  ß: "ss",
  æ: "ae",
  œ: "oe",
  ø: "o",
};

/**
 * The ONE slug rule (API derives spot slugs with it). Lowercase, umlauts transliterated,
 * other diacritics stripped, non-alphanumerics → "-", max 80 chars cut at a hyphen.
 * Returns "" when nothing usable remains — callers must fall back (e.g. "spot").
 */
export function slugify(input: string): string {
  const s = input
    .toLowerCase()
    .replace(/[äöüßæœø]/g, (c) => TRANSLIT[c] ?? c)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (s.length <= 80) return s;
  const cut = s.slice(0, 80);
  const i = cut.lastIndexOf("-");
  return (i > 40 ? cut.slice(0, i) : cut).replace(/-+$/, "");
}

export const Timestamp = z.iso.datetime({ offset: true });

/**
 * Publication status of spots/objects. `pending` is unused since diver suggestions live in
 * their own queue (suggestions.ts); kept so existing rows and clients stay valid.
 */
export const Status = z.enum(["draft", "published", "pending"]);
export type Status = z.infer<typeof Status>;

/** `diver` = a self-registered account that may only suggest; staff = moderator | admin. */
export const Role = z.enum(["admin", "moderator", "diver"]);
export type Role = z.infer<typeof Role>;
export const STAFF_ROLES: readonly Role[] = ["admin", "moderator"];
export const isStaff = (role: Role): boolean => STAFF_ROLES.includes(role);

/** Calendar date YYYY-MM-DD (Europe/Zurich), e.g. a sighting day. */
export const IsoDate = z.iso.date();

export const ErrorCode = z.enum([
  "bad_request",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "rate_limited",
  /** 413: request body over the route's limit (only the admin model upload today). */
  "payload_too_large",
  "internal",
]);

/** Every non-2xx JSON response from /api (except better-auth's own /api/auth/*). */
export const ApiError = z
  .object({
    error: z.object({
      code: ErrorCode,
      message: z.string(),
      details: z.unknown().optional(),
    }),
  })
  .strict();
export type ApiError = z.infer<typeof ApiError>;
